import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faArrowRightLong,
  faMagnifyingGlass,
  faChevronDown,
  faAngleLeft,
  faAngleRight,
  faAnglesLeft,
  faAnglesRight,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import styles from "./SyncReview.module.css";
import {
  GabRowResolutionType,
  RowDiff,
  SyncSession,
  TableDiff,
  nameFieldFor,
  rowKey,
} from "./types";
import { BulkTarget, ResolutionMap, isResolvable } from "./resolutionModel";

// One table's worth of rows renders at a time (chosen from the left rail) and is paged, so a large
// diff never mounts more than PAGE_SIZE row elements — the redesign's answer to "so much data".
const PAGE_SIZE = 50;

// Shared 6-column track for the header, the filter row and every data row so they stay aligned.
const GRID_COLS = "110px 72px minmax(148px,1fr) minmax(168px,1.3fr) 250px 40px";

const CONFLICT_TEXT: Record<string, string> = {
  NullBusinessKey: "Missing key",
  DuplicateTargetKey: "Duplicate key in target",
  MissingParent: "Parent not in target",
};

type Status = "New" | "Changed" | "Unchanged" | "Conflict";

function statusOf(row: RowDiff): Status {
  if (!row.isApplicable) return "Conflict";
  return row.changeType;
}

function truncate(value: string | null, max: number): string {
  const s = value == null ? "" : String(value);
  return s.length > max ? s.slice(0, max - 1) + "…" : s;
}

function changedColumns(row: RowDiff): string[] {
  return row.columns.filter((c) => c.differs).map((c) => c.column);
}

interface Props {
  session: SyncSession;
  resolutions: ResolutionMap;
  reviewedKeys: Set<string>;
  fieldErrors: Record<string, Record<string, string>>;
  showUnchanged: boolean;
  submitting: boolean;
  onToggleUnchanged: (value: boolean) => void;
  onResolve: (key: string, value: GabRowResolutionType) => void;
  onBulk: (tableCode: string, target: BulkTarget, value: GabRowResolutionType) => void;
  onClear: (keys: string[]) => void;
  onSaveCustom: (key: string, row: RowDiff, payload: Record<string, string | null>) => void;
  onDiscard: () => void;
  onRunSync: () => void;
}

function SyncReview({
  session,
  resolutions,
  reviewedKeys,
  fieldErrors,
  showUnchanged,
  submitting,
  onToggleUnchanged,
  onResolve,
  onBulk,
  onClear,
  onSaveCustom,
  onDiscard,
  onRunSync,
}: Props) {
  const tables = session.tables;
  const [activeCode, setActiveCode] = useState<string>(tables[0]?.tableCode ?? "");
  const [tableQuery, setTableQuery] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fKey, setFKey] = useState("");
  const [fName, setFName] = useState("");
  const [page, setPage] = useState(1);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Record<string, string>>({});
  const [detailView, setDetailView] = useState<"side" | "changed">("side");
  const [summaryOpen, setSummaryOpen] = useState(false);

  const activeTable: TableDiff = useMemo(
    () => tables.find((t) => t.tableCode === activeCode) ?? tables[0],
    [tables, activeCode]
  );

  // Reset paging / open panels whenever the visible slice changes underneath them.
  useEffect(() => {
    setPage(1);
  }, [activeCode, fStatus, fKey, fName, showUnchanged]);

  // Keep the summary drawer open while the sync is submitting so its "Saving…" state stays visible;
  // close it once the submit settles. On success the whole review unmounts anyway; on failure closing
  // reveals the row errors underneath.
  const wasSubmitting = useRef(false);
  useEffect(() => {
    if (wasSubmitting.current && !submitting) setSummaryOpen(false);
    wasSubmitting.current = submitting;
  }, [submitting]);

  // ── Global + per-table progress (a row counts once the user has explicitly touched it) ──
  const progress = useMemo(() => {
    let decided = 0;
    let total = 0;
    const perTable: Record<string, { decided: number; total: number }> = {};
    for (const t of tables) {
      let td = 0;
      let tt = 0;
      for (const r of t.rows) {
        if (!isResolvable(r)) continue;
        tt += 1;
        if (reviewedKeys.has(rowKey(r.tableCode, r.keyValue))) td += 1;
      }
      perTable[t.tableCode] = { decided: td, total: tt };
      decided += td;
      total += tt;
    }
    return { decided, total, pct: total ? Math.round((decided / total) * 100) : 0, perTable };
  }, [tables, reviewedKeys]);

  // ── Summary drawer buckets, across every table ──
  const summary = useMemo(() => {
    const b = { takeSource: 0, takeNew: 0, edited: 0, keep: 0, skip: 0, pending: 0 };
    for (const t of tables) {
      for (const r of t.rows) {
        if (!isResolvable(r)) continue;
        const key = rowKey(r.tableCode, r.keyValue);
        const res = resolutions[key] ?? "Keep";
        const isNew = r.changeType === "New";
        if (res === "Custom") b.edited += 1;
        else if (res === "TakeSource") isNew ? (b.takeNew += 1) : (b.takeSource += 1);
        else isNew ? (b.skip += 1) : (b.keep += 1);
        if (!reviewedKeys.has(key)) b.pending += 1;
      }
    }
    return b;
  }, [tables, resolutions, reviewedKeys]);

  const railGroups = useMemo(() => {
    const q = tableQuery.trim().toLowerCase();
    return tables.filter(
      (t) => !q || t.tableName.toLowerCase().includes(q) || t.tableCode.toLowerCase().includes(q)
    );
  }, [tables, tableQuery]);

  const filteredRows = useMemo(() => {
    return activeTable.rows.filter((r) => {
      const status = statusOf(r);
      if (status === "Unchanged" && !showUnchanged) return false;
      if (fStatus && status.toLowerCase() !== fStatus) return false;
      if (fKey && !String(r.keyValue ?? "").includes(fKey.trim())) return false;
      if (fName) {
        const name = r.sourcePayload[nameFieldFor(r.tableCode)] ?? r.targetValues?.[nameFieldFor(r.tableCode)] ?? "";
        if (!name.toLowerCase().includes(fName.trim().toLowerCase())) return false;
      }
      return true;
    });
  }, [activeTable, showUnchanged, fStatus, fKey, fName]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageRows = filteredRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const nameField = nameFieldFor(activeTable.tableCode);

  const switchTable = (code: string) => {
    setActiveCode(code);
    setExpandedKey(null);
    setEditingKey(null);
  };

  const toggleExpand = (key: string) => {
    setExpandedKey((k) => (k === key ? null : key));
    if (editingKey && editingKey !== key) setEditingKey(null);
  };

  const startEdit = (key: string, row: RowDiff) => {
    const seed: Record<string, string> = {};
    for (const col of changedColumns(row)) seed[col] = row.sourcePayload[col] ?? "";
    setEditDraft(seed);
    setEditingKey(key);
    setExpandedKey(key);
  };

  const saveEdit = (key: string, row: RowDiff) => {
    // The custom payload must be the COMPLETE row (all compare columns + key + parent key), not just
    // the edited columns: the apply path writes it verbatim and the server validates every synced
    // column (a missing non-nullable column fails validation / would be written null). Start from the
    // full source payload and layer the user's edits on top.
    const payload: Record<string, string | null> = { ...row.sourcePayload, ...editDraft };
    onSaveCustom(key, row, payload);
    setEditingKey(null);
  };

  const shownResolvableKeys = () =>
    filteredRows.filter(isResolvable).map((r) => rowKey(r.tableCode, r.keyValue));

  const source = session.sourceLabel;
  const target = session.targetLabel;

  return (
    <div className={styles.review}>
      {/* ── TOP BAR ─────────────────────────────────────────────── */}
      <header className={styles.topbar}>
        <div className={styles.route}>
          <span className={styles.brandLabel}>Sync</span>
          <span className={`${styles.pill} ${styles.pillSource}`}>{source}</span>
          <FontAwesomeIcon icon={faArrowRightLong} className={styles.routeArrow} />
          <span className={`${styles.pill} ${styles.pillTarget}`} title={target}>
            {target}
          </span>
        </div>
        <div className={styles.spacer} />
        <div className={styles.progress}>
          <div className={styles.progressText}>
            <div>
              <span className={styles.progressDone}>{progress.decided}</span>{" "}
              <span className={styles.progressOf}>of</span> {progress.total}
            </div>
            <div className={styles.progressCaption}>differences reviewed</div>
          </div>
          <div className={styles.progressTrack}>
            <span className={styles.progressFill} style={{ width: `${progress.pct}%` }} />
          </div>
        </div>
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={showUnchanged}
            onChange={(e) => onToggleUnchanged(e.target.checked)}
          />
          Show unchanged
        </label>
        <button type="button" className={styles.btnDiscard} onClick={onDiscard}>
          <FontAwesomeIcon icon={faXmark} /> Discard
        </button>
        <button type="button" className={styles.btnPrimary} onClick={() => setSummaryOpen(true)}>
          Continue to summary <FontAwesomeIcon icon={faArrowRightLong} />
        </button>
      </header>

      {/* ── BODY ────────────────────────────────────────────────── */}
      <div className={styles.body}>
        {/* LEFT RAIL: TABLES */}
        <aside className={styles.rail}>
          <div className={styles.railHead}>
            <div className={styles.railTitle}>Tables · {tables.length}</div>
            <div className={styles.railSearch}>
              <FontAwesomeIcon icon={faMagnifyingGlass} className={styles.railSearchIcon} />
              <input
                className={styles.railSearchInput}
                placeholder="Filter tables"
                value={tableQuery}
                onChange={(e) => setTableQuery(e.target.value)}
              />
            </div>
          </div>
          <div className={styles.railList}>
            {railGroups.map((t) => {
              const p = progress.perTable[t.tableCode] ?? { decided: 0, total: 0 };
              const pct = p.total ? Math.round((p.decided / p.total) * 100) : 0;
              const done = p.total > 0 && p.decided === p.total;
              const active = t.tableCode === activeTable.tableCode;
              return (
                <button
                  key={t.tableCode}
                  type="button"
                  className={`${styles.railItem} ${active ? styles.railItemActive : ""}`}
                  onClick={() => switchTable(t.tableCode)}
                >
                  <div className={styles.railItemTop}>
                    <span className={styles.railItemName}>{t.tableName}</span>
                    <span className={styles.railItemAcr}>{t.tableCode}</span>
                  </div>
                  <div className={styles.railItemCounts}>
                    <span className={styles.cNew}>{t.newCount} new</span>
                    <span className={styles.cChanged}>{t.changedCount} chg</span>
                    {t.conflictCount > 0 && (
                      <span className={styles.cConflict}>{t.conflictCount} conflict</span>
                    )}
                  </div>
                  <div className={styles.railItemProgress}>
                    <div className={styles.railBarTrack}>
                      <span
                        className={styles.railBarFill}
                        style={{ width: `${pct}%`, background: done ? "#2f7d4f" : "#c9a227" }}
                      />
                    </div>
                    <span className={styles.railItemFrac}>
                      {p.decided}/{p.total}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* MAIN */}
        <main className={styles.main}>
          <div className={styles.groupHeader}>
            <h1 className={styles.groupTitle}>{activeTable.tableName}</h1>
            <span className={styles.groupAcr}>({activeTable.tableCode})</span>
            <div className={styles.spacer} />
            <span className={styles.cNew}>{activeTable.newCount} new</span>
            <span className={styles.cChanged}>{activeTable.changedCount} changed</span>
            <span className={styles.cConflict}>{activeTable.conflictCount} conflict</span>
            <span className={styles.cMuted}>{activeTable.unchangedCount} unchanged</span>
          </div>

          <div className={styles.toolbar}>
            <span className={styles.bulkLabel}>Bulk</span>
            <button
              type="button"
              className={styles.btnBulkGreen}
              onClick={() => onBulk(activeTable.tableCode, "new", "TakeSource")}
            >
              Take all new
            </button>
            <button
              type="button"
              className={styles.btnBulkGreen}
              onClick={() => onBulk(activeTable.tableCode, "changed", "TakeSource")}
            >
              Take all source
            </button>
            <button
              type="button"
              className={styles.btnBulkGrey}
              onClick={() => onBulk(activeTable.tableCode, "all", "Keep")}
            >
              Keep all target
            </button>
            <button
              type="button"
              className={styles.btnClear}
              onClick={() => onClear(shownResolvableKeys())}
            >
              Clear
            </button>
            <span className={styles.bulkNote}>applies to {filteredRows.length} shown</span>
          </div>

          <div className={styles.card}>
            {/* column header */}
            <div className={styles.colHeader} style={{ gridTemplateColumns: GRID_COLS }}>
              <div>Status</div>
              <div>{activeTable.keyColumn}</div>
              <div>Name</div>
              <div>What changed</div>
              <div>Resolution</div>
              <div />
            </div>
            {/* filter row */}
            <div className={styles.filterRow} style={{ gridTemplateColumns: GRID_COLS }}>
              <div className={styles.filterCell}>
                <select className={styles.select} value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
                  <option value="">All</option>
                  <option value="new">New</option>
                  <option value="changed">Changed</option>
                  <option value="conflict">Conflict</option>
                </select>
              </div>
              <div className={styles.filterCell}>
                <input className={styles.input} placeholder="#" value={fKey} onChange={(e) => setFKey(e.target.value)} />
              </div>
              <div className={styles.filterCell}>
                <input className={styles.input} placeholder="Filter name" value={fName} onChange={(e) => setFName(e.target.value)} />
              </div>
              <div />
              <div />
              <div />
            </div>

            {/* rows */}
            <div className={styles.rowsScroll}>
              {pageRows.map((row) => (
                <SyncRow
                  key={rowKey(row.tableCode, row.keyValue)}
                  row={row}
                  rowK={rowKey(row.tableCode, row.keyValue)}
                  nameField={nameField}
                  resolution={resolutions[rowKey(row.tableCode, row.keyValue)] ?? "Keep"}
                  reviewed={reviewedKeys.has(rowKey(row.tableCode, row.keyValue))}
                  expanded={expandedKey === rowKey(row.tableCode, row.keyValue)}
                  editing={editingKey === rowKey(row.tableCode, row.keyValue)}
                  editDraft={editingKey === rowKey(row.tableCode, row.keyValue) ? editDraft : null}
                  errors={fieldErrors[rowKey(row.tableCode, row.keyValue)] ?? {}}
                  detailView={detailView}
                  sourceLabel={source}
                  onResolve={onResolve}
                  onToggle={toggleExpand}
                  onStartEdit={startEdit}
                  onEditChange={(col, v) => setEditDraft((d) => ({ ...d, [col]: v }))}
                  onSaveEdit={saveEdit}
                  onCancelEdit={() => setEditingKey(null)}
                  onDetailView={setDetailView}
                />
              ))}

              {filteredRows.length === 0 && (
                <div className={styles.emptyState}>
                  <FontAwesomeIcon icon={faMagnifyingGlass} className={styles.emptyIcon} />
                  <div className={styles.emptyTitle}>No differences match your filters</div>
                  <div className={styles.emptyHint}>Try clearing the status or name filter.</div>
                </div>
              )}
            </div>

            {/* pager */}
            <div className={styles.pager}>
              <div className={styles.pagerBtns}>
                <button type="button" className={styles.pagerBtn} disabled={safePage <= 1} onClick={() => setPage(1)}>
                  <FontAwesomeIcon icon={faAnglesLeft} />
                </button>
                <button type="button" className={styles.pagerBtn} disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}>
                  <FontAwesomeIcon icon={faAngleLeft} />
                </button>
                <span className={styles.pagerCurrent}>{safePage}</span>
                <span className={styles.pagerTotal}>/ {pageCount}</span>
                <button type="button" className={styles.pagerBtn} disabled={safePage >= pageCount} onClick={() => setPage(safePage + 1)}>
                  <FontAwesomeIcon icon={faAngleRight} />
                </button>
                <button type="button" className={styles.pagerBtn} disabled={safePage >= pageCount} onClick={() => setPage(pageCount)}>
                  <FontAwesomeIcon icon={faAnglesRight} />
                </button>
              </div>
              <div className={styles.pagerInfo}>
                Showing {filteredRows.length} of this table · {progress.decided} reviewed across all tables
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* ── SUMMARY DRAWER ──────────────────────────────────────── */}
      {summaryOpen && (
        <div className={styles.drawerOverlay} onClick={() => setSummaryOpen(false)}>
          <div className={styles.drawerPanel} onClick={(e) => e.stopPropagation()}>
            <div className={styles.drawerHead}>
              <div className={styles.drawerHeadRow}>
                <h2 className={styles.drawerTitle}>Sync summary</h2>
                <button type="button" className={styles.drawerClose} onClick={() => setSummaryOpen(false)}>
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
              <p className={styles.drawerDesc}>
                These resolutions will be written from <b className={styles.txtSource}>{source}</b> into{" "}
                <b className={styles.txtTarget}>{target}</b> when you run the sync.
              </p>
            </div>
            <div className={styles.drawerBody}>
              <SummaryRow color="#2f7d4f" label="Take source — overwrite target" n={summary.takeSource} />
              <SummaryRow color="#4fa46a" label="Take new — insert record" n={summary.takeNew} />
              <SummaryRow color="#b07d1c" label="Edited manually" n={summary.edited} />
              <SummaryRow color="#3a5578" label="Keep target — no change" n={summary.keep} />
              <SummaryRow color="#8a97a6" label="Skip — leave new record out" n={summary.skip} />
              <SummaryRow color="#c05b57" label="Not reviewed — applies its default" n={summary.pending} />
              {summary.pending > 0 && (
                <div className={styles.pendingWarn}>
                  {summary.pending} difference(s) are still unreviewed. They'll apply their default (new records
                  inserted, changed rows kept). Close this to review them first.
                </div>
              )}
            </div>
            <div className={styles.drawerFoot}>
              <button
                type="button"
                className={styles.btnBack}
                disabled={submitting}
                onClick={() => setSummaryOpen(false)}
              >
                Back to review
              </button>
              <button type="button" className={styles.btnRun} disabled={submitting} onClick={onRunSync}>
                {submitting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Saving resolutions…
                  </>
                ) : (
                  <>Run sync →</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Summary drawer line ───────────────────────────────────────────
function SummaryRow({ color, label, n }: { color: string; label: string; n: number }) {
  return (
    <div className={styles.sumRow}>
      <span className={styles.sumDot} style={{ background: color }} />
      <span className={styles.sumLabel}>{label}</span>
      <span className={styles.sumN}>{n}</span>
    </div>
  );
}

// ── One data row (status/key/name/what-changed/resolution + expandable detail & inline edit) ──
interface RowProps {
  row: RowDiff;
  rowK: string;
  nameField: string;
  resolution: GabRowResolutionType;
  reviewed: boolean;
  expanded: boolean;
  editing: boolean;
  editDraft: Record<string, string> | null;
  errors: Record<string, string>;
  detailView: "side" | "changed";
  sourceLabel: string;
  onResolve: (key: string, value: GabRowResolutionType) => void;
  onToggle: (key: string) => void;
  onStartEdit: (key: string, row: RowDiff) => void;
  onEditChange: (col: string, value: string) => void;
  onSaveEdit: (key: string, row: RowDiff) => void;
  onCancelEdit: () => void;
  onDetailView: (v: "side" | "changed") => void;
}

const SyncRow = React.memo(function SyncRow({
  row,
  rowK,
  nameField,
  resolution,
  reviewed,
  expanded,
  editing,
  editDraft,
  errors,
  detailView,
  sourceLabel,
  onResolve,
  onToggle,
  onStartEdit,
  onEditChange,
  onSaveEdit,
  onCancelEdit,
  onDetailView,
}: RowProps) {
  const status = statusOf(row);
  const isNew = status === "New";
  const isConflict = status === "Conflict";
  const resolvable = isResolvable(row);

  // Name previews the chosen outcome so Keep/Take source reads as old-vs-new:
  //   Keep → the current (target) name; Take source / Edit → the incoming (source) name.
  //   New rows always show the incoming name (muted + struck when skipped, i.e. Keep).
  const sourceName = row.sourcePayload[nameField] ?? "";
  const targetName = row.targetValues?.[nameField] ?? "";
  const keeping = resolution === "Keep";
  const skippingNew = isNew && keeping;
  const name = !isNew && keeping ? targetName || sourceName : sourceName || targetName;
  const nameWillChange = !isNew && !!sourceName && !!targetName && sourceName !== targetName;

  const changed = changedColumns(row);
  const chips = isNew ? [] : changed.slice(0, 3);
  const more = !isNew && changed.length > 3 ? `+${changed.length - 3} more` : "";
  const primary = !isNew && changed.length ? changed[0] : "";

  const pill =
    status === "New"
      ? { t: "New", cls: styles.pillNew }
      : status === "Conflict"
      ? { t: "Conflict", cls: styles.pillConflict }
      : status === "Unchanged"
      ? { t: "Unchanged", cls: styles.pillUnchanged }
      : { t: "Changed", cls: styles.pillChanged };

  const accent = reviewed ? "#2f7d4f" : isConflict ? "#b23b39" : resolvable ? "#e6c46a" : "#e2e8ef";

  // Detail: source vs target per column, optionally filtered to changed-only.
  const detailRows = (detailView === "changed" ? row.columns.filter((c) => c.differs) : row.columns).map((c) => ({
    col: c.column,
    source: truncate(c.sourceValue, 90),
    target: truncate(c.targetValue, 90),
    changed: c.differs,
  }));
  const diffLabel = isNew
    ? `New record — ${row.columns.length} fields will be inserted`
    : `${changed.length} of ${row.columns.length} columns differ`;

  const seg = (label: string, value: GabRowResolutionType, activeColor: string, active: boolean) => (
    <button
      type="button"
      className={styles.seg}
      style={active ? { background: activeColor, color: "#fff" } : undefined}
      onClick={() => onResolve(rowK, value)}
    >
      {label}
    </button>
  );

  return (
    <div className={styles.row} style={{ borderLeft: `3px solid ${accent}`, background: expanded ? "#f7fafd" : "#fff" }}>
      <div className={styles.rowGrid} style={{ gridTemplateColumns: GRID_COLS }}>
        <div className={styles.cellStatus}>
          <span className={`${styles.statusPill} ${pill.cls}`}>{pill.t}</span>
        </div>
        <div className={styles.cellKey}>{row.keyValue}</div>
        <div className={styles.cellName}>
          <span className={skippingNew ? styles.nameSkipped : undefined}>{name}</span>
          {nameWillChange && (
            <span className={keeping ? styles.nameTagCurrent : styles.nameTagNew}>
              {keeping ? "current" : "new"}
            </span>
          )}
        </div>

        {/* what changed */}
        <div className={styles.cellChanged}>
          {isNew ? (
            <span>
              <span className={styles.newTag}>+ New record</span>
              <span className={styles.cMuted}> · {row.columns.length} fields</span>
            </span>
          ) : primary ? (
            <>
              <div className={styles.diffLine}>
                <span className={styles.diffCol}>{primary}</span>
                <span className={styles.diffFrom}>{truncate(row.columns.find((c) => c.column === primary)?.targetValue ?? "", 22)}</span>
                <span className={styles.diffArrow}>→</span>
                <span className={styles.diffTo}>{truncate(row.columns.find((c) => c.column === primary)?.sourceValue ?? "", 26)}</span>
              </div>
              {chips.length > 0 && (
                <div className={styles.chips}>
                  {chips.map((c) => (
                    <span key={c} className={styles.chip}>
                      {c}
                    </span>
                  ))}
                  {more && <span className={styles.chipMore}>{more}</span>}
                </div>
              )}
            </>
          ) : isConflict ? (
            <span className={styles.conflictText}>
              {row.conflicts.length ? CONFLICT_TEXT[row.conflicts[0]] ?? row.conflicts[0] : "Conflict"}
            </span>
          ) : (
            <span className={styles.cMuted}>—</span>
          )}
        </div>

        {/* resolution */}
        <div className={styles.cellRes}>
          {!resolvable ? (
            isConflict ? (
              <span className={styles.resNone}>
                {row.conflicts.length
                  ? CONFLICT_TEXT[row.conflicts[0]] ?? row.conflicts[0]
                  : "Not applicable"}
              </span>
            ) : (
              <span className={styles.cMuted}>—</span>
            )
          ) : (
            <div className={styles.segGroup}>
              {isNew ? (
                <>
                  {seg("Skip", "Keep", "#8a97a6", resolution === "Keep")}
                  {seg("Take new", "TakeSource", "#2f7d4f", resolution === "TakeSource")}
                </>
              ) : (
                <>
                  {seg("Keep", "Keep", "#3a5578", resolution === "Keep")}
                  {seg("Take source", "TakeSource", "#2f7d4f", resolution === "TakeSource")}
                  <button
                    type="button"
                    className={`${styles.seg} ${errors && Object.keys(errors).length ? styles.segError : ""}`}
                    style={resolution === "Custom" ? { background: "#b07d1c", color: "#fff" } : undefined}
                    title="Edit values before applying"
                    onClick={() => onStartEdit(rowK, row)}
                  >
                    Edit
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* expand */}
        <div className={styles.cellExpand}>
          <button
            type="button"
            className={styles.expandBtn}
            title="Compare columns"
            style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}
            onClick={() => onToggle(rowK)}
          >
            <FontAwesomeIcon icon={faChevronDown} />
          </button>
        </div>
      </div>

      {/* expanded detail */}
      {expanded && (
        <div className={styles.detail}>
          <div className={styles.detailHead}>
            <span className={styles.detailLabel}>{diffLabel}</span>
            <div className={styles.spacer} />
            <div className={styles.viewTabs}>
              <button
                type="button"
                className={`${styles.viewTab} ${detailView === "side" ? styles.viewTabActive : ""}`}
                onClick={() => onDetailView("side")}
              >
                Side by side
              </button>
              <button
                type="button"
                className={`${styles.viewTab} ${detailView === "changed" ? styles.viewTabActive : ""}`}
                onClick={() => onDetailView("changed")}
              >
                Changed only
              </button>
            </div>
          </div>

          <div className={styles.detailTable}>
            <div className={styles.detailHeadRow}>
              <div className={styles.detailColName}>Column</div>
              <div className={styles.detailSource}>Source · {sourceLabel}</div>
              <div className={styles.detailTarget}>Target · current</div>
            </div>
            {detailRows.map((f) => (
              <div
                key={f.col}
                className={styles.detailRow}
                style={{ background: f.changed ? "#fdf6e6" : "#fff" }}
              >
                <div className={styles.detailColCell}>
                  {f.changed && <span className={styles.detailDot} />}
                  {f.col}
                </div>
                <div className={f.changed ? styles.detailValChangedSrc : styles.detailValMuted}>{f.source}</div>
                <div className={f.changed ? styles.detailValChangedTgt : styles.detailValMuted}>{f.target}</div>
              </div>
            ))}
          </div>

          {/* inline edit */}
          {editing && (
            <div className={styles.editPanel}>
              <div className={styles.editTitle}>Edit values · prefilled from source, adjust before applying</div>
              {changed.map((col) => (
                <div key={col} className={styles.editField}>
                  <label className={styles.editLabel}>{col}</label>
                  <div className={styles.editInputWrap}>
                    <input
                      className={styles.editInput}
                      value={editDraft?.[col] ?? row.sourcePayload[col] ?? ""}
                      onChange={(e) => onEditChange(col, e.target.value)}
                    />
                    {errors[col] && <span className={styles.fieldError}>{errors[col]}</span>}
                  </div>
                </div>
              ))}
              <div className={styles.editActions}>
                <button type="button" className={styles.btnApplyEdit} onClick={() => onSaveEdit(rowK, row)}>
                  Apply edited values
                </button>
                <button type="button" className={styles.btnCancelEdit} onClick={onCancelEdit}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export { SyncReview };
